import types from "./types";
import { firestore, USERS_COLL, ORG_COLL, fieldValue } from "_firebase";
import moment from "moment";
import { checkAllowPayments } from "utils/payment/helpers";
import { collectUndefinedPaths, removeUndefined } from "utils/generalUtils/generalUtils";
import { captureCodeLibFirestoreError } from "utils/sentry/captureCodeLibFirestoreError";
import { captureCodeLibUndefinedSanitized } from "utils/sentry/captureCodeLibUndefinedSanitized";
import { isOrgCodeRef, getOrgIdFromCodeRef } from "utils/codes/refs";

const getUser = (uid) => firestore.doc(`${USERS_COLL}/${uid}`).get();

const getUserDir = (uid) => firestore.doc(`${USERS_COLL}/${uid}`).collection("modelDir").doc("directory").get();

const getUserCommDir = (uid) => firestore.doc(`${USERS_COLL}/${uid}`).collection("commDir").doc("directory").get();

const getBatchDir = (uid) => firestore.doc(`${USERS_COLL}/${uid}`).collection("batchDir").doc("directory").get();

const getUserCodeLib = (uid) => firestore.collection(`${USERS_COLL}/${uid}/codeLib`).get();

// An organization's library mirrors upgradesLib: one document per code, keyed by its ref, rather
// than the per component type map the personal library uses.
const getOrgCodeLib = (orgId) => firestore.collection(`${ORG_COLL}/${orgId}/codeLib`).get();

const orgCodeDoc = (orgId, codeRef) => firestore.doc(`${ORG_COLL}/${orgId}/codeLib/${codeRef}`);

// Organization codes are slotted into the personal library's shape, so every consumer of
// `user.codeLib` sees them without knowing they came from elsewhere. Refs carry their origin, so
// nothing needs a parallel flag to route an edit or a delete later.
const mergeOrgCodesIntoLib = (codeLibObj, orgCodeDocs) =>
    orgCodeDocs.reduce((lib, doc) => {
        const code = doc.data() || {};
        const { componentType = "" } = code;

        if (!componentType) return lib;

        return {
            ...lib,
            [componentType]: {
                ...(lib[componentType] || {}),
                codes: { ...(lib[componentType]?.codes || {}), [doc.id]: { ...code, codeRef: doc.id } },
            },
        };
    }, codeLibObj);

const getUserParametricDir = (uid) =>
    firestore.doc(`${USERS_COLL}/${uid}`).collection("parametricDir").doc("directory").get();

const getUserCodeLibOverflow = (uid, codeType) =>
    firestore.collection(`${USERS_COLL}/${uid}/codeLib/${codeType}/overflowCodes`).get();

const sendCodeToLib = (uid, code, componentType, codeRef, lastDocId = "") =>
    firestore
        .doc(`${USERS_COLL}/${uid}/codeLib/${componentType}${lastDocId !== "" ? "/overflowCodes/" + lastDocId : ""}`)
        .update({
            [`codes.${codeRef}`]: code,
        });

const deleteFromCodeLib = ({ uid, componentType, codeRef }) =>
    firestore
        .doc(`${USERS_COLL}/${uid}/codeLib/${componentType}`)
        .update({
            [`codes.${codeRef}`]: fieldValue.delete(),
        })
        .catch((err) => console.log("err", err.message));

const updateUserData = (uid, updates) => {
    return firestore.doc(`${USERS_COLL}/${uid}`).update(updates);
};

const updateUserMetaInFirebase = (uid, updates) => {
    return firestore.doc(`${USERS_COLL}/${uid}`).update({
        userMeta: updates,
    });
};

const setUserData = (userData) => ({
    type: types.GET_USER_DATA,
    userData,
});

const setUserDir = (modelDir) => ({
    type: types.GET_USER_DIR,
    modelDir,
});

const setUserCommDir = (commDir) => ({
    type: types.GET_USER_COMM_DIR,
    commDir,
});

const setBatchDir = (batchDir) => ({
    type: types.GET_USER_BATCH_DIR,
    batchDir,
});

const removeFromDir = (modelId) => ({
    type: types.REMOVE_FROM_DIR,
    modelId,
});

const updateModelInDir = (modelId, updates = {}) => ({
    type: types.UPDATE_MODEL_IN_DIR,
    modelId,
    updates,
});

const removeFromCommDir = (communityId) => ({
    type: types.REMOVE_FROM_COMM_DIR,
    communityId,
});

const setUserCodeLib = (codeLib) => ({
    type: types.GET_USER_CODE_LIB,
    codeLib,
});

const userStart = () => ({
    type: types.GET_USER_DATA_START,
});

const userFail = (error) => ({
    type: types.GET_USER_DATA_FAIL,
    error,
});

const updateUser = (data) => ({
    type: types.UPDATE_USER,
    data,
});

const updateUserMeta = (data) => ({
    type: types.UPDATE_USER_META,
    data,
});

const updateUserPreferences = (data) => ({
    type: types.UPDATE_USER_PREFERENCES,
    data,
});

const toggleDirLoading = (modelDirLoading) => ({
    type: types.TOGGLE_DIR_LOADING,
    modelDirLoading,
});

const toggleCommDirLoading = (commDirLoading) => ({
    type: types.TOGGLE_COMM_DIR_LOADING,
    commDirLoading,
});

const toggleBatchDirLoading = (batchDirLoading) => ({
    type: types.TOGGLE_BATCH_DIR_LOADING,
    batchDirLoading,
});

const setCodeInLib = (code, componentType, codeRef) => ({
    type: types.SET_CODE_IN_LIB,
    code,
    componentType,
    codeRef,
});

const removeCodeFromLib = (componentType, codeRef) => ({
    type: types.REMOVE_CODE_FROM_LIB,
    componentType,
    codeRef,
});

const fetchParametricDirStart = () => ({
    type: types.FETCH_PARAMETRIC_ANALYSIS_DIR_START,
});

const fetchParametricDirSuccess = (parametricDir) => ({
    type: types.FETCH_PARAMETRIC_ANALYSIS_DIR_SUCCESS,
    payload: parametricDir,
});

const fetchParametricDirFail = (error) => ({
    type: types.FETCH_PARAMETRIC_ANALYSIS_DIR_FAIL,
    payload: error,
});

const toggleParametricAnalysisDirLoading = (loading) => ({
    type: types.TOGGLE_PARAMETRIC_ANALYSIS_DIR_LOADING,
    payload: loading,
});

const fetchUserData = (uid) => async (dispatch) => {
    try {
        if (!uid) return null;

        const userRef = await getUser(uid);

        if (!userRef.exists) {
            throw new Error("User does not exist");
        }

        const user = userRef.data();
        const allowPayments = await checkAllowPayments();

        await dispatch(setUserData({ ...user, allowPayments }));

        return { ...user, allowPayments };
    } catch (error) {
        await dispatch(userFail("There was a problem fetching user data"));

        return "There was a problem fetching user data";
    }
};

const fetchUserDir =
    (uid, skipLoading = false) =>
    (dispatch) => {
        if (!uid) return null;

        if (!skipLoading) dispatch(toggleDirLoading(true));

        return getUserDir(uid)
            .then((data) => {
                return dispatch(setUserDir(data.data()));
            })
            .then((data) => dispatch(toggleDirLoading(false)))
            .catch(() => dispatch(userFail("There was a problem fetching user directory")));
    };

const fetchParametricDir =
    (uid, skipLoading = false) =>
    async (dispatch) => {
        try {
            if (!uid) return null;

            dispatch(fetchParametricDirStart());

            if (!skipLoading) dispatch(toggleParametricAnalysisDirLoading(true));

            const data = await getUserParametricDir(uid);

            dispatch(toggleParametricAnalysisDirLoading(false));

            dispatch(fetchParametricDirSuccess(data.data()));
        } catch (error) {
            console.log("error", error.message);
            dispatch(fetchParametricDirFail(error));
        }
        // return getUserDir(uid)
        //     .then((data) => {
        //         return dispatch(setUserDir(data.data()));
        //     })
        //     .then((data) => dispatch(toggleDirLoading(false)))
        //     .catch(() => dispatch(userFail("There was a problem fetching user directory")));
    };

const fetchUserCommDir =
    (uid, skipLoading = false) =>
    (dispatch) => {
        if (!uid) return null;

        if (!skipLoading) dispatch(toggleCommDirLoading(true));

        return getUserCommDir(uid)
            .then((data) => {
                const commDir = data.data() || { folders: {}, communities: {} };
                return dispatch(setUserCommDir(commDir));
            })
            .then((data) => dispatch(toggleCommDirLoading(false)))
            .catch(() => dispatch(userFail("There was a problem fetching user's community directory")));
    };

const fetchUserBatchDir = (uid) => (dispatch) => {
    if (!uid) return null;

    dispatch(toggleBatchDirLoading(true));
    return getBatchDir(uid)
        .then((data) => dispatch(setBatchDir(data.data())))
        .then((data) => dispatch(toggleBatchDirLoading(false)))
        .catch(() => dispatch(userFail("There was a problem fetching user's batch directory")));
};

// Memberships are read rather than passed, so none of the callers have to know about them. State
// is the cheap source, but the login flow fetches the code library before the user data lands, so
// an empty state falls back to the user document, which is where memberships come from anyway.
const readOrgIds = async (uid, getState) => {
    const fromState = getState()?.organization?.memberOrgIds;
    const { primaryOrg = "", ...memberOrgs } = fromState || (await getUser(uid))?.data()?.organization || {};

    return [
        ...new Set(
            [primaryOrg, ...Object.keys(memberOrgs).filter((id) => typeof memberOrgs[id] === "object")].filter(Boolean)
        ),
    ];
};

const fetchUserCodeLib = (uid) => (dispatch, getState) => {
    if (!uid) return null;

    let codeLibObj = {};

    return getUserCodeLib(uid)
        .then(({ docs }) => {
            const docObj = docs.reduce(
                (prev, current) => ({
                    ...prev,
                    [current.id]: current.data(),
                }),
                {}
            );

            codeLibObj = docObj;

            return {};
        })
        .then(() => {
            return Promise.all(
                Object.keys(codeLibObj).map(async (codeType) => {
                    const codeLibOverflowSnapshot = await getUserCodeLibOverflow(uid, codeType);
                    return codeLibOverflowSnapshot.docs.map((doc) => {
                        const { codes } = doc.data() || {};

                        codeLibObj[codeType].codes = {
                            ...codeLibObj[codeType].codes,
                            ...codes,
                        };

                        return {};
                    });
                })
            );
        })
        .then(async () => {
            const orgIds = await readOrgIds(uid, getState);

            //A failed organization library must not cost the user their own
            const orgSnapshots = await Promise.all(
                orgIds.map((orgId) =>
                    getOrgCodeLib(orgId).catch((err) => {
                        console.error(`Error fetching code library for organization ${orgId}:`, err.message);

                        return { docs: [] };
                    })
                )
            );

            codeLibObj = orgSnapshots.reduce((lib, { docs = [] }) => mergeOrgCodesIntoLib(lib, docs), codeLibObj);
        })
        .then(() => {
            dispatch(setUserCodeLib(codeLibObj));
        })
        .catch((err) => {
            console.log("err", err.message);
            dispatch(userFail("There was a problem fetching user code library"));

            return "There was a problem fetching user code library";
        });
};

const addToCodeLib =
    (uid, code, componentType, codeRef, orgId = "") =>
    (dispatch, getState) => {
        if (!uid) return null;

        const lastEdited = moment().format("LLLL");
        const lastEditedBy = uid;

        const codePayload = { ...code, lastEdited, lastEditedBy };
        const undefinedPaths = collectUndefinedPaths(codePayload);
        const updatedCode = removeUndefined(codePayload);
        const user = getState().user;

        undefinedPaths.forEach((fieldPath) => {
            captureCodeLibUndefinedSanitized({
                fieldPath,
                undefinedPaths,
                uid,
                componentType,
                codeRef,
                user,
            });
        });

        //One document per code, so there is no map to read first and no overflow to place it in
        if (orgId) {
            return orgCodeDoc(orgId, codeRef)
                .set({ ...updatedCode, componentType, codeRef })
                .then(() => dispatch(setCodeInLib({ ...updatedCode, componentType, codeRef }, componentType, codeRef)))
                .catch(() => dispatch(userFail("There was a problem saving code to the organization library")));
        }

        return getUserCodeLibOverflow(uid, componentType)
            .then((snapshot) => {
                let lastDocId = "";
                snapshot.docs.forEach((doc) => {
                    const { codes = {} } = doc.data();
                    if (Object.keys(codes).length < 10) {
                        lastDocId = doc.id;
                    }
                });

                return lastDocId;
            })
            .then((lastDocId) => {
                return sendCodeToLib(uid, updatedCode, componentType, codeRef, lastDocId);
            })
            .then(() => dispatch(setCodeInLib(updatedCode, componentType, codeRef)))
            .catch((err) => {
                captureCodeLibFirestoreError(err, {
                    operation: "save",
                    uid,
                    componentType,
                    codeRef,
                    user,
                });
                dispatch(userFail("There was a problem saving code to library"));
            });
    };

const deleteCode = (uid, codeRef, componentType) => (dispatch, getState) => {
    if (!uid) return null;

    //Organization codes carry their origin in the ref, so nothing extra has to be passed in
    if (isOrgCodeRef(codeRef)) {
        return orgCodeDoc(getOrgIdFromCodeRef(codeRef), codeRef)
            .delete()
            .then(() => dispatch(removeCodeFromLib(componentType, codeRef)))
            .catch((err) => {
                console.error(`Error deleting code ${codeRef} from the organization library:`, err.message);

                return dispatch(userFail("There was a problem removing code from the organization library"));
            });
    }

    return deleteFromCodeLib({ uid, componentType, codeRef })
        .then(() => dispatch(removeCodeFromLib(componentType, codeRef)))
        .catch((err) => {
            captureCodeLibFirestoreError(err, {
                operation: "delete",
                uid,
                componentType,
                codeRef,
                user: getState().user,
            });
            dispatch(userFail("There was a problem removing code from library"));
        });
};

const addUserTestingInfo =
    ({ uid, name, userMeta = {} }) =>
    (dispatch) => {
        if (!uid) return null;

        return updateUserData(uid, { name, userMeta })
            .then(() => dispatch(updateUser({ name })))
            .then(() => dispatch(updateUserMeta(userMeta)));
    };

const addUserPreferences =
    ({ uid, name, userMeta = {}, userPreferences = {} }) =>
    (dispatch, getState) => {
        if (!uid) return null;

        const newUserMeta = {
            ...getState().user.userMeta,
            ...userMeta,
        };

        const newUserPreferences = {
            ...getState().user.userPreferences,
            ...userPreferences,
        };

        return updateUserData(uid, {
            name,
            userMeta: newUserMeta,
            userPreferences: newUserPreferences,
        })
            .then(() => dispatch(updateUser({ name })))
            .then(() => dispatch(updateUserMeta(newUserMeta)))
            .then(() => dispatch(updateUserPreferences(newUserPreferences)));
    };

const saveUserMeta =
    ({ uid, userMeta = {} }) =>
    (dispatch, getState) => {
        if (!uid) return null;

        const newUserMeta = {
            ...getState().user.userMeta,
            ...userMeta,
        };

        // Update local state immediately so tour replay isn't racing a Firebase round-trip.
        dispatch(updateUserMeta(newUserMeta));
        return updateUserMetaInFirebase(uid, newUserMeta);
    };

const updateUserPrimaryOrg = (uid, orgId) => async (dispatch) => {
    try {
        dispatch({ type: types.UPDATE_USER_PRIMARY_ORG_START });

        await firestore.doc(`${USERS_COLL}/${uid}`).set({ organization: { primaryOrg: orgId } }, { merge: true });

        await dispatch(fetchUserData(uid));

        dispatch({ type: types.UPDATE_USER_PRIMARY_ORG_SUCCESS });
    } catch (error) {
        dispatch({ type: types.UPDATE_USER_PRIMARY_ORG_FAIL, payload: error });
    }
};

const confirmUserEmail = (uid) => async (dispatch) => {
    try {
        dispatch({ type: types.CONFIRM_USER_EMAIL_START });

        await firestore.doc(`${USERS_COLL}/${uid}`).set({ isEmailConfirmed: true }, { merge: true });
        await dispatch(fetchUserData(uid));

        dispatch({ type: types.CONFIRM_USER_EMAIL_SUCCESS });
    } catch (error) {
        dispatch({ type: types.CONFIRM_USER_EMAIL_FAIL, payload: error });
    }
};

const saveFavouriteHiddenRows =
    ({ uid, favouriteHiddenRows = {} }) =>
    (dispatch, getState) => {
        if (!uid) return null;

        const newUserPreferences = {
            ...getState().user.userPreferences,
            favouriteHiddenRows,
        };

        return updateUserData(uid, {
            userPreferences: newUserPreferences,
        }).then(() => dispatch(updateUserPreferences(newUserPreferences)));
    };

export default {
    fetchUserData,
    fetchUserDir,
    fetchUserCommDir,
    fetchUserBatchDir,
    fetchUserCodeLib,
    userFail,
    userStart,
    addToCodeLib,
    removeFromDir,
    updateModelInDir,
    removeFromCommDir,
    addUserTestingInfo,
    deleteCode,
    saveUserMeta,
    updateUserData,
    addUserPreferences,
    fetchParametricDir,
    getUserParametricDir,
    updateUserPrimaryOrg,
    updateUser,
    confirmUserEmail,
    saveFavouriteHiddenRows,
};
